import { isNil } from 'ramda';
import { type SetRequired } from 'type-fest';

import * as alumniPage from '@/app/l/[region]/[language]/alumni/[[...pagination]]/alumni.urls';
import * as applyFormPage from '@/app/l/[region]/[language]/apply/[...extraParams]/apply.urls';
import * as apprenticeshipPage from '@/app/l/[region]/[language]/apprenticeship/[course]/apprenticeship.urls';
import * as b2bPage from '@/app/l/[region]/[language]/b2b/b2b.urls';
import * as b2bApplyPage from '@/app/l/[region]/[language]/b2b/apply/b2b-apply.urls';
import * as blogHomepage from '@/app/l/[region]/[language]/blog/[[...pagination]]/blog.urls';
import * as blogArticlePage from '@/app/l/[region]/[language]/blog-article/[slug]/blog-article.urls';
import * as blogAuthorPage from '@/app/l/[region]/[language]/blog-author/[author]/[[...pagination]]/blog-author.urls';
import * as campusPage from '@/app/l/[region]/[language]/campus/[campus]/[[...extraParams]]/campus.urls';
import * as careerPage from '@/app/l/[region]/[language]/career/career.urls';
import * as contactFormPage from '@/app/l/[region]/[language]/contact/contact.urls';
import * as coursePage from '@/app/l/[region]/[language]/course/[course]/[[...extraParams]]/course.urls';
import * as faqPage from '@/app/l/[region]/[language]/faq/faq.urls';
import * as financingPage from '@/app/l/[region]/[language]/financing/financing.urls';
import * as financingFormPage from '@/app/l/[region]/[language]/financing-form/financing-form.urls';
import * as fullApplyPage from '@/app/l/[region]/[language]/full-apply/full-apply.urls';
import * as genericPage from '@/app/l/[region]/[language]/generic-page/[...urlSegments]/generic-page.urls';
import * as homepage from '@/app/l/[region]/[language]/home/[[...extraParams]]/home.urls';
import * as iframeEmbed from '@/app/l/[region]/[language]/iframe-embed/[...urlSegments]/iframe-embed.urls';
import * as pdp from '@/app/l/[region]/[language]/pdp/[course]/[campus]/[[...extraParams]]/pdp.urls';
import * as quizPage from '@/app/l/[region]/[language]/quiz/quiz.urls';
import * as quizFlowPage from '@/app/l/[region]/[language]/quiz-flow/[...urlSegments]/quiz-flow.urls';
import * as quizMatchPage from '@/app/l/[region]/[language]/quiz-match/[course]/quiz-match.urls';
import * as richTextPage from '@/app/l/[region]/[language]/rich-text/[...urlSegments]/rich-text.urls';
import * as syllabusFormPage from '@/app/l/[region]/[language]/syllabus/[course]/syllabus.urls';
import * as thankYouPage from '@/app/l/[region]/[language]/thank-you/thank-you.urls';
import * as deRegion from '@/app/urls/de';
import * as deEnRegion from '@/app/urls/de-en';
import * as esRegion from '@/app/urls/es';
import * as esEnRegion from '@/app/urls/es-en';
import * as frRegion from '@/app/urls/fr';
import * as frEnRegion from '@/app/urls/fr-en';
import * as gbRegion from '@/app/urls/gb';
import * as nlRegion from '@/app/urls/nl';
import * as nlEnRegion from '@/app/urls/nl-en';
import * as ptRegion from '@/app/urls/pt';
import * as ptEnRegion from '@/app/urls/pt-en';
import * as usRegion from '@/app/urls/us';
import * as wwRegion from '@/app/urls/ww';
import config from '@/config';
import { isValidSalesforceId } from '@/lib/salesforce';
import { toCamelCase } from '@/lib/utils';
import { type Language, regionMainLanguage } from '@/lib/utils/language';
import type { ApprenticeshipPages } from '@/app/l/[region]/[language]/apprenticeship/[course]/apprenticeship.urls';
import type { CampusPages } from '@/app/l/[region]/[language]/campus/[campus]/[[...extraParams]]/campus.urls';
import type { CoursePages } from '@/app/l/[region]/[language]/course/[course]/[[...extraParams]]/course.urls';
import type { IframePages } from '@/app/l/[region]/[language]/iframe-embed/[...urlSegments]/iframe-embed.urls';
import type { PdpPages } from '@/app/l/[region]/[language]/pdp/[course]/[campus]/[[...extraParams]]/pdp.urls';
import type { QuizMatchPages } from '@/app/l/[region]/[language]/quiz-match/[course]/quiz-match.urls';
import type { QuizFlowPages } from '@/app/l/[region]/[language]/quiz-flow/[...urlSegments]/quiz-flow.urls';
import type { RichTextPages } from '@/app/l/[region]/[language]/rich-text/[...urlSegments]/rich-text.urls';
import type { SyllabusPages } from '@/app/l/[region]/[language]/syllabus/[course]/syllabus.urls';
import type {
  AvailableCourseTrack,
  Campus,
  Region,
  StandardishRegion,
  UrlEntry,
  UrlRegion,
} from '@/types';

export type PageType =
  | 'alumni'
  | 'apply'
  | 'b2b'
  | 'b2bApply'
  | 'blog'
  | 'blogArticle'
  | 'blogAuthor'
  | 'career'
  | 'contact'
  | 'faq'
  | 'financing'
  | 'financingForm'
  | 'fullApply'
  | 'genericPage'
  | 'home'
  | 'quiz'
  | 'thankYou'
  | ApprenticeshipPages
  | CampusPages
  | CoursePages
  | IframePages
  | PdpPages
  | QuizFlowPages
  | QuizMatchPages
  | RichTextPages
  | SyllabusPages;

// SimplePageType needs to match the page's folder name for the sitemap to work
// Could be interesting to enforce that more directly in TypeScript
export type SimplePageType =
  | Exclude<
      PageType,
      | ApprenticeshipPages
      | CampusPages
      | CoursePages
      | IframePages
      | PdpPages
      | QuizFlowPages
      | QuizMatchPages
      | RichTextPages
      | SyllabusPages
    >
  | 'apprenticeship'
  | 'campus'
  | 'course'
  | 'iframeEmbed'
  | 'pdp'
  | 'quizFlow'
  | 'quizMatch'
  | 'richText'
  | 'syllabus';

export const internalToRouteMapping: Record<
  PageType,
  Partial<Record<UrlRegion, UrlEntry>>
> = {
  ...alumniPage.urls,
  ...apprenticeshipPage.urls,
  ...applyFormPage.urls,
  ...b2bApplyPage.urls,
  ...b2bPage.urls,
  ...blogArticlePage.urls,
  ...blogAuthorPage.urls,
  ...blogHomepage.urls,
  ...campusPage.urls,
  ...careerPage.urls,
  ...contactFormPage.urls,
  ...coursePage.urls,
  ...faqPage.urls,
  ...financingFormPage.urls,
  ...financingPage.urls,
  ...fullApplyPage.urls,
  ...genericPage.urls,
  ...homepage.urls,
  ...iframeEmbed.urls,
  ...pdp.urls,
  ...quizPage.urls,
  ...quizFlowPage.urls,
  ...quizMatchPage.urls,
  ...richTextPage.urls,
  ...syllabusFormPage.urls,
  ...thankYouPage.urls,
};

export const normalizeUrlEntry = (entry: UrlEntry): Exclude<UrlEntry, string> =>
  typeof entry === 'string' ? { url: entry, isIndexable: true } : entry;

export type BasePageParams<T = string[]> = {
  extraParams?: T;
  language: Language;
  region: Region;
};

export const getUrlRegion = (params: BasePageParams): UrlRegion => {
  const { language, region } = params;
  const mainLanguage = regionMainLanguage[region];
  const standardishRegion = region === 'uk' ? 'gb' : region;

  return language === mainLanguage
    ? standardishRegion
    : (`${standardishRegion}-${language}` as UrlRegion);
};

export const splitUrlRegion = (urlRegion: UrlRegion): BasePageParams => {
  const [tempRegion, tempLanguage] = urlRegion.split('-') as [
    StandardishRegion,
    Language?
  ];
  const region = tempRegion === 'gb' ? 'uk' : tempRegion;
  const language = tempLanguage ?? regionMainLanguage[region];

  return { language, region };
};

export type PossibleParams = {
  author: string;
  campus: Campus;
  cohortId: string;
  course: AvailableCourseTrack;
  pagination: string[];
  slug: string;
  timezone: string[];
  urlSegments: string[];
  userCountry: string;
};

export type FullParams = BasePageParams & Partial<PossibleParams>;

export const getPageUrl = (
  page: PageType,
  params: FullParams,
  options?: { withOrigin: boolean }
): string | undefined => {
  const { withOrigin } = { withOrigin: false, ...options };

  const urlRegion = getUrlRegion(params);
  const urlEntry = internalToRouteMapping[page]?.[urlRegion];
  if (isNil(urlEntry)) return;

  let pathname = `/${urlRegion}`;

  const localizedPart = normalizeUrlEntry(urlEntry).url;
  if (localizedPart) pathname += `/${localizedPart}`;

  const { author, cohortId, pagination, slug, urlSegments } = params;
  if (page === 'apply') {
    if (!cohortId || !isValidSalesforceId(cohortId)) return;
    pathname += `/c/${cohortId}`;
  }

  if (page === 'blogArticle') {
    if (!slug) return;
    pathname += `/${slug}`;
  }

  if (page === 'blogAuthor') {
    if (!author) return;
    pathname += `/${author}`;
  }

  if (page === 'genericPage') {
    if (!urlSegments || urlSegments.length === 0) return;
    pathname += `/${urlSegments.join('/')}`;
  }

  const urlTypes = ['iframeEmbed', 'quizFlow', 'richText'] as const;
  const isUrlType = urlTypes.some((urlType) => page.startsWith(urlType));
  if (isUrlType) {
    if (!urlSegments || urlSegments.length === 0) return;
    const joinedSegments = urlSegments.slice(1).join('/');
    if (joinedSegments) pathname += `/${joinedSegments}`;
  }

  if (pagination) {
    const pageNumber = pagination.find((part) =>
      Number.isInteger(Number(part))
    );
    if (pageNumber) pathname += `/page/${pageNumber}`;
  }
  const origin = withOrigin ? config.baseUrl : '';

  return `${origin}${pathname}`;
};

export const urlToRouteMapping: Record<
  UrlRegion,
  Record<
    string,
    `/l/${Region}/${Language}` | `/l/${Region}/${Language}/${string}`
  >
> = {
  ...deRegion.urls,
  ...deEnRegion.urls,
  ...esRegion.urls,
  ...esEnRegion.urls,
  ...frRegion.urls,
  ...frEnRegion.urls,
  ...gbRegion.urls,
  ...nlRegion.urls,
  ...nlEnRegion.urls,
  ...ptRegion.urls,
  ...ptEnRegion.urls,
  ...usRegion.urls,
  ...wwRegion.urls,
};

type UrlConfig = {
  canonicalUrlArgs?: (
    t: PageType,
    p: FullParams
  ) => [PageType, FullParams] | Promise<[PageType, FullParams]>;
  isIndexable?: boolean;
};

const pageTypeConfig = {
  alumni: alumniPage,
  apply: applyFormPage,
  apprenticeship: apprenticeshipPage,
  b2b: b2bPage,
  b2bApply: b2bApplyPage,
  blog: blogHomepage,
  blogArticle: blogArticlePage,
  blogAuthor: blogAuthorPage,
  campus: campusPage,
  career: careerPage,
  contact: contactFormPage,
  course: coursePage,
  faq: faqPage,
  financing: financingPage,
  financingForm: financingFormPage,
  fullApply: fullApplyPage,
  genericPage,
  home: homepage,
  iframeEmbed,
  pdp,
  quiz: quizPage,
  quizFlow: quizFlowPage,
  quizMatch: quizMatchPage,
  richText: richTextPage,
  syllabus: syllabusFormPage,
  thankYou: thankYouPage,
} as Record<SimplePageType, UrlConfig>;

const regionConfig = {
  de: deRegion,
  'de-en': deEnRegion,
  es: esRegion,
  'es-en': esEnRegion,
  fr: frRegion,
  'fr-en': frEnRegion,
  gb: gbRegion,
  nl: nlRegion,
  'nl-en': nlEnRegion,
  pt: ptRegion,
  'pt-en': ptEnRegion,
  us: usRegion,
  ww: wwRegion,
} as Record<UrlRegion, UrlConfig>;

export const normalizeUrlConfig = (
  urlConfig: UrlConfig
): SetRequired<UrlConfig, 'isIndexable'> => ({
  isIndexable: true,
  ...urlConfig,
});

const internalUrlToPage: Record<string, PageType> = {
  'b2b/apply': 'b2bApply',
};
export const getPageFromInternalRoute = (internalRoute: string) => {
  // The first 9 characters of the internal route correspond to /l/xx/yy/
  const page = internalRoute.slice(9) as PageType;
  return internalUrlToPage[page] ?? toCamelCase(page);
};

export const isPageIndexable = (
  page: PageType,
  params: FullParams
): boolean => {
  const urlRegion = getUrlRegion(params);
  const urlEntry = internalToRouteMapping[page]?.[urlRegion];
  if (isNil(urlEntry)) return false;

  const simplePage = page.split('/')[0] as SimplePageType;
  const entryLevel = normalizeUrlEntry(urlEntry);
  const pageTypeLevel = normalizeUrlConfig(pageTypeConfig[simplePage]);
  const regionLevel = normalizeUrlConfig(regionConfig[urlRegion]);

  return (
    Boolean(entryLevel.isIndexable) &&
    pageTypeLevel.isIndexable &&
    regionLevel.isIndexable
  );
};

export const getCanonicalUrlArgs = async (
  pageType: PageType,
  params: FullParams
): Promise<[PageType, FullParams]> => {
  const urlRegion = getUrlRegion(params);

  const simplePage = pageType.split('/')[0] as SimplePageType;
  const pageTypeLevel = normalizeUrlConfig(pageTypeConfig[simplePage]);
  const regionLevel = normalizeUrlConfig(regionConfig[urlRegion]);

  const [regionType, regionParams] = (await regionLevel.canonicalUrlArgs?.(
    pageType,
    params
  )) ?? [pageType, params];
  const [finalType, finalParams] = (await pageTypeLevel.canonicalUrlArgs?.(
    regionType,
    regionParams
  )) ?? [regionType, regionParams];

  return [finalType, finalParams];
};
